import { useEffect, useState, useRef } from "react";

import { PROFILE_UI_CONTENT } from "./profileContextContent";
import { resolveProfileUIText } from "./resolveProfileUIText";

import {
  getFamilyValues,
  getFamilyTypes,
  getFamilyStatuses,
  submitFamilyDetails,
  submitSiblingDetails,
  getAutoWriteContent,
  submitFamilyOriginAbout,
  submitAboutLifePartner,
} from "../../javaAxiosConfig";

import RegistrationStepLayout from "./RegistrationStepLayout";
import dayjs from "dayjs";

// ICONS (reuse same family icon everywhere)
import familyIcon from "../../images/profilecreatedby.png";

import aboutPartnerIcon from "../../images/aboutPartner.png";

import infoIcon from "../../images/i-buton.png";

import threestar from "../../images/neeStars.png";
import { display } from "@mui/system";

// =====================================================
// PARENT FLOW
// =====================================================

const FamilyDetailsFlow = ({ onCompleted }) => {
  const [step, setStep] = useState(1);

  const [slideClass, setSlideClass] = useState("");
const prevStepRef = useRef(step);


  const [showPopup, setShowPopup] = useState(false);
  const [popupMessage, setPopupMessage] = useState("");

  const [parentInfo, setParentInfo] = useState({
    fatherName: "",
    fatherProfession: "",
    motherName: "",
    motherProfession: "",
  });

  const [familyValue, setFamilyValue] = useState("");
  const [familyType, setFamilyType] = useState("");
  const [familyStatus, setFamilyStatus] = useState("");

  const [aboutFamily, setAboutFamily] = useState({
    familyOrigin: "",
    description: "",
  });

  useEffect(() => {
  if (prevStepRef.current === step) return;

  const isForward = step > prevStepRef.current;

  setSlideClass(isForward ? "slide-forward" : "slide-backward");

  const timer = setTimeout(() => {
    setSlideClass("");
  }, 500); // premium smooth 450–500ms

  prevStepRef.current = step;

  return () => clearTimeout(timer);
}, [step]);


  const next = () => setStep((s) => s + 1);
  const back = () => setStep((s) => Math.max(1, s - 1));

  const profileCreatedById = Number(localStorage.getItem("profileCreatedById"));
  const gender = localStorage.getItem("selectedGender");

  const aboutMeText = resolveProfileUIText({
    profileCreatedById,
    gender,
    stepKey: "ABOUT_ME",
    stepConfig: PROFILE_UI_CONTENT.ABOUT_ME,
  });

  const aboutPartnerText = resolveProfileUIText({
    profileCreatedById,
    gender,
    stepKey: "ABOUT_PARTNER",
    stepConfig: PROFILE_UI_CONTENT.ABOUT_PARTNER,
  });

  const getResolvedFamilyText = (stepKey) => {
    const stepConfig = PROFILE_UI_CONTENT?.[stepKey];
    if (!stepConfig) return { title: "", subtitle: "" };

    return resolveProfileUIText({
      profileCreatedById,
      gender,
      stepKey,
      stepConfig,
    });
  };

  const parentsText = getResolvedFamilyText("PARENTS_INFO");
  const familyBgText = getResolvedFamilyText("FAMILY_BACKGROUND");
  const siblingsText = getResolvedFamilyText("SIBLINGS");
  const aboutFamilyText = getResolvedFamilyText("ABOUT_FAMILY");
  const aboutMePartnerText = getResolvedFamilyText("ABOUT_ME_PARTNER");

  const infoFamilyOptionalText = getResolvedFamilyText("INFO_FAMILY_OPTIONAL");

  return (
    <>
    <div className={`registration-anim-wrapper ${slideClass}`}>
      {step === 1 && (
        <ParentsInformationStep
          title={parentsText.title}
          subtitle={parentsText.subtitle}
          infoText={infoFamilyOptionalText.subtitle}
          onBack={back}
          onNext={(data) => {
            setParentInfo(data);
            next();
          }}
        />
      )}

      {step === 2 && (
        <FamilyBackgroundStep
          title={familyBgText.title}
          subtitle={familyBgText.subtitle}
          onBack={back}
          onNext={async (data) => {
            // 🔥 Build exact backend payload
            const payload = {
              fatherName: parentInfo.fatherName,
              fatherProfessions: parentInfo.fatherProfession,
              motherName: parentInfo.motherName,
              motherProfession: parentInfo.motherProfession,
              familyValueId: data.familyValueId,
              familyTypeId: data.familyTypeId,
              familyStatusId: data.familyStatusId || "", // backend allows empty
            };

            // 🔥 CALL API HERE
            await submitFamilyDetails(payload);

            // 🔥 Move forward
            next(); // goes to Siblings
          }}
        />
      )}

      {step === 3 && (
        <SiblingsStep
          title={siblingsText.title}
          subtitle={siblingsText.subtitle}
          onBack={back}
          onNext={async (data) => {
            const payload = {
              marriedSister:
                data.marriedSisters !== 0 ? String(data.marriedSisters) : "",
              unMarriedSister:
                data.unmarriedSisters !== 0
                  ? String(data.unmarriedSisters)
                  : "",
              marriedBrothers:
                data.marriedBrothers !== 0 ? String(data.marriedBrothers) : "",
              unMarriedBrothers:
                data.unmarriedBrothers !== 0
                  ? String(data.unmarriedBrothers)
                  : "",
            };

            await submitSiblingDetails(payload);
            next(); // 👉 go to About Family
          }}
        />
      )}

      {step === 4 && (
        <AboutFamilyStep
          title={aboutFamilyText.title}
          subtitle={aboutFamilyText.subtitle}
          onBack={back}
          onSubmit={() => next()}
          showPopup={(msg) => {
            setPopupMessage(msg);
            setShowPopup(true);
          }}
        />
      )}

      {step === 5 && (
        <AboutMePartnerStep
          title={aboutMePartnerText.title}
          subtitle={aboutMePartnerText.subtitle}
          aboutMeText={aboutMeText}
          aboutPartnerText={aboutPartnerText}
          onBack={back}
          onCompleted={onCompleted}
          showPopup={(msg) => {
            setPopupMessage(msg);
            setShowPopup(true);
          }}
        />
      )}

      {/* ✅ POPUP MUST LIVE HERE */}
      {showPopup && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.5)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 9999,
          }}
          onClick={() => setShowPopup(false)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: "#fff",
              borderRadius: 12,
              padding: 20,
              width: "90%",
              maxWidth: 380,
              textAlign: "center",
            }}
          >
            <h3 style={{ color: "#dc3545", marginBottom: 10 }}>
              Auto write issue
            </h3>

            <p style={{ fontSize: 14 }}>{popupMessage}</p>

            <button
              onClick={() => setShowPopup(false)}
              style={{
                marginTop: 14,
                padding: "8px 20px",
                borderRadius: 20,
                border: "none",
                background: "#dc3545",
                color: "#fff",
                cursor: "pointer",
              }}
            >
              OK
            </button>
          </div>
        </div>
      )}

      </div>
    </>
  );
};

export default FamilyDetailsFlow;

const FloatingInput = ({ label, placeholder, value, onChange }) => {
  return (
    <div style={{ marginTop: 16, position: "relative", width: "100%" }}>
      {/* Input container */}
      <div
        style={{
          height: 56,
          borderRadius: 13,
          border: "1px solid #e8e6ea",
          padding: "14px 16px 0",
          boxSizing: "border-box",
          position: "relative",
        }}
      >
        {/* Floating label */}
        <div
          style={{
            position: "absolute",
            top: -9,
            left: 14,
            background: "#fff",
            padding: "0 6px",
            fontSize: 13,
            fontWeight: 600,
          }}
        >
          {label}
        </div>

        {/* Input */}
        <input
          value={value}
          placeholder={placeholder}
          onChange={(e) => onChange(e.target.value)}
          style={{
            width: "100%",
            border: "none",
            outline: "none",
            fontSize: 18,
            marginTop: 5,
            color: "#555",
            background: "transparent",
          }}
        />
      </div>
    </div>
  );
};

//
// =====================================================
// STEP 1 – PARENTS INFORMATION
// =====================================================
//

const ParentsInformationStep = ({
  onBack,
  onNext,
  title,
  subtitle,
  infoText,
}) => {
  const [form, setForm] = useState({
    fatherName: "",
    fatherProfession: "",
    motherName: "",
    motherProfession: "",
  });

  const sanitizeParentName = (value) => {
    // relaxed typing-time validation: letters, space, dot
    if (!/^[A-Za-z .]*$/.test(value)) return null;

    if (value.length === 0) return value;

    // Auto-capitalize first character
    return value.charAt(0).toUpperCase() + value.slice(1);
  };

  const update = (key, val) => {
    if (key === "fatherName" || key === "motherName") {
      const sanitized = sanitizeParentName(val);
      if (sanitized === null) return; // block invalid input
      setForm((p) => ({ ...p, [key]: sanitized }));
      return;
    }

    setForm((p) => ({ ...p, [key]: val }));
  };

  return (
    <RegistrationStepLayout
      icon={familyIcon}
      // title="Parent’s Information"
      // subtitle="Share your parents’ names and profession."
      title={title}
      subtitle={subtitle}
      stepText="Step 1 of 5"
      // onBack={onBack}
      onContinue={() => onNext(form)}
    >
      <FloatingInput
        label="Father’s Name"
        placeholder="Enter father’s full name"
        value={form.fatherName}
        onChange={(v) => update("fatherName", v)}
      />

      <FloatingInput
        label="Father’s Profession"
        placeholder="Enter father’s profession"
        value={form.fatherProfession}
        onChange={(v) => update("fatherProfession", v)}
      />

      <FloatingInput
        label="Mother’s Name"
        placeholder="Enter mother’s full name"
        value={form.motherName}
        onChange={(v) => update("motherName", v)}
      />

      <FloatingInput
        label="Mother’s Profession"
        placeholder="Enter mother’s profession"
        value={form.motherProfession}
        onChange={(v) => update("motherProfession", v)}
      />

      {/* INFO BOX */}
      <div
        style={{
          marginTop: 15,
          padding: "16px 18px",
          border: "1px dashed rgba(230,0,0,0.59)",
          borderRadius: 10,
          fontSize: 12,
          color: "rgba(82,81,81,0.8)",
          display: "flex",
          alignItems: "center",
          gap: 10,
        }}
      >
        <img
          src={infoIcon}
          alt="info"
          style={{
            width: 22,
            height: 22,
            flexShrink: 0,
            marginTop: 0,
          }}
        />
        {/* Family details are optional. You may update them anytime from Edit */}
        {infoText}
      </div>

      {/* SKIP */}
      <div
        onClick={() => onNext(form)}
        style={{
          marginTop: 15,
          width: "100%",
          height: 50,
          borderRadius: 50,
          border: "1px solid transparent",
          background:
            "linear-gradient(#fff, #fff) padding-box, linear-gradient(90deg, rgba(255,119,5,0.88), rgba(230,0,0,0.81)) border-box",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontWeight: 600,
          color: "rgba(230,0,0,0.81)",
          cursor: "pointer",
        }}
      >
        Skip for now
      </div>
    </RegistrationStepLayout>
  );
};

// =====================================================
// FAMILY BACKGROUND – COMMON OPTION STEP
// =====================================================

const FamilyBackgroundStep = ({ onBack, onNext, title, subtitle }) => {
  const [familyValues, setFamilyValues] = useState([]);
  const [familyTypes, setFamilyTypes] = useState([]);
  const [familyStatuses, setFamilyStatuses] = useState([]);

  const handleSkip = () => {
    onNext({
      familyValueId: selected.familyValueId || "",
      familyTypeId: selected.familyTypeId || "",
      familyStatusId: selected.familyStatusId || "",
    });
  };

  const handleContinue = () => {
    if (subStep < steps.length - 1) {
      // 👉 move to next option screen
      setSubStep((prev) => prev + 1);
    } else {
      // 👉 last screen → submit & move forward
      onNext({
        familyValueId: selected.familyValueId || "",
        familyTypeId: selected.familyTypeId || "",
        familyStatusId: selected.familyStatusId || "",
      });
    }
  };

  useEffect(() => {
    getFamilyValues().then((res) =>
      setFamilyValues((res.data || []).filter((i) => i.isActive)),
    );

    getFamilyTypes().then((res) =>
      setFamilyTypes((res.data || []).filter((i) => i.isActive)),
    );

    getFamilyStatuses().then((res) =>
      setFamilyStatuses((res.data || []).filter((i) => i.isActive)),
    );
  }, []);

  const steps = [
    {
      title: "Select your Family Value",
      list: familyValues,
      labelKey: "value",
      storeKey: "familyValueId",
    },
    {
      title: "Select your Family Type",
      list: familyTypes,
      labelKey: "type",
      storeKey: "familyTypeId",
    },
    {
      title: "Select your Family Status",
      list: familyStatuses,
      labelKey: "status",
      storeKey: "familyStatusId",
    },
  ];

  const [subStep, setSubStep] = useState(0);

  const [selected, setSelected] = useState({
    familyValueId: "",
    familyTypeId: "",
    familyStatusId: "",
  });

  const handleSelect = (item) => {
    const key = steps[subStep].storeKey;

    setSelected((prev) => ({
      ...prev,
      [key]: item.id,
    }));

    // 🔥 ONLY move screen, DO NOT submit
    if (subStep < steps.length - 1) {
      setSubStep((prev) => prev + 1);
    }
  };

  return (
    <RegistrationStepLayout
      icon={familyIcon}
      // title="Family Background"
      // subtitle="Share your family background."

      title={title}
      subtitle={subtitle}
      stepText="Step 2 of 5"
      onBack={onBack}
      onContinue={handleContinue}
    >
      {/* TITLE */}
      <div
        style={{
          textAlign: "center",
          alignSelf: "center",
          fontSize: 18,
          fontWeight: 600,
          marginTop: 5,
          marginBottom: 10,
          color: "#444",
        }}
      >
        {steps[subStep].title}
      </div>

      {/* OPTIONS */}

      {/* {steps[subStep].list.map((item) => {
  const active =
    selected[steps[subStep].storeKey] === item.id;

  return (
    <div
      key={item.id}
      onClick={() => handleSelect(item)}
      style={{
        height: 54,
        borderRadius: 50,
        width:"100%",
        marginBottom: 14,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        cursor: "pointer",
        fontWeight: active ? 600 : 500,
        border: active ? "1.5px solid #FF5A2C" : "1px solid #E6E6E6",
        color: active ? "#FF5A2C" : "#666",
      }}
    >
      {item[steps[subStep].labelKey]}
    </div>
  );
})} */}

      <div
        style={{
          maxHeight: 65 * 4 + 14 * 3, // 👈 exactly 4 options visible
          overflowY: "auto",
          width: "100%",
          paddingRight: 6, // 👈 space for scrollbar
        }}
      >
        {steps[subStep].list.map((item) => {
          const active = selected[steps[subStep].storeKey] === item.id;

          return (
            <div
              key={item.id}
              onClick={() => handleSelect(item)}
              style={{
                height: 54,
                borderRadius: 50,
                width: "100%",
                marginBottom: 14,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                fontWeight: active ? 600 : 500,
                border: active ? "1.5px solid #FF5A2C" : "1px solid #E6E6E6",
                color: active ? "#FF5A2C" : "#666",
                flexShrink: 0, // 👈 IMPORTANT for scrolling
              }}
            >
              {item[steps[subStep].labelKey]}
            </div>
          );
        })}
      </div>

      {/* DOT INDICATOR */}
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          width: "100%",
          gap: 8,
          marginTop: 10,
        }}
      >
        {steps.map((_, i) => (
          <div
            key={i}
            onClick={() => setSubStep(i)}
            style={{
              height: 8,
              alignSelf: "center",
              width: subStep === i ? 36 : 28,
              borderRadius: 8,
              background: subStep === i ? "#FF7A2F" : "#EAEAEA",
              transition: "all 0.3s ease",
              cursor: "pointer",
            }}
          />
        ))}
      </div>

      {/* SKIP FOR NOW */}
      <div
        onClick={handleSkip}
        style={{
          marginTop: 10,
          width: "100%",
          height: 50,
          borderRadius: 50,
          border: "1px solid transparent",
          background:
            "linear-gradient(#fff, #fff) padding-box, linear-gradient(90deg, rgba(255,119,5,0.88), rgba(230,0,0,0.81)) border-box",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontWeight: 600,
          color: "rgba(230,0,0,0.81)",
          cursor: "pointer",
        }}
      >
        Skip for now
      </div>
    </RegistrationStepLayout>
  );
};

// =====================================================
// STEP 3 sibling step
// =====================================================

const SiblingsStep = ({ onBack, onNext, title, subtitle }) => {
  const MIN = 0;
  const MAX = 5;

  const [counts, setCounts] = useState({
    marriedSisters: 0,
    unmarriedSisters: 0,
    marriedBrothers: 0,
    unmarriedBrothers: 0,
  });

  const handleSkip = () => {
    onNext(counts);
  };

  const Counter = ({ label, value, onChange }) => {
    const minusDisabled = value === MIN;
    const plusDisabled = value === MAX;

    return (
      <div style={{ marginBottom: 10, width: "100%" }}>
        {/* TITLE */}
        <div
          style={{
            fontSize: 17,
            fontWeight: 600,
            textAlign: "center",
            marginBottom: 5,
            fontFamily: "Montserrat",
          }}
        >
          {label}
        </div>

        {/* COUNTER */}
        <div
          style={{
            height: 56,
            borderRadius: 30,
            border: "1.5px solid #E6E6E6",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "0 25px",
          }}
        >
          <button
            disabled={minusDisabled}
            onClick={() => !minusDisabled && onChange(value - 1)}
            style={{
              fontSize: 28,
              border: "none",
              background: "transparent",
              cursor: minusDisabled ? "not-allowed" : "pointer",
              color: minusDisabled ? "#CFCFCF" : "#000",
            }}
          >
            –
          </button>

          <span style={{ fontSize: 20, fontWeight: 500 }}>{value}</span>

          <button
            disabled={plusDisabled}
            onClick={() => !plusDisabled && onChange(value + 1)}
            style={{
              fontSize: 28,
              border: "none",
              background: "transparent",
              cursor: plusDisabled ? "not-allowed" : "pointer",
              color: plusDisabled ? "#CFCFCF" : "#000",
            }}
          >
            +
          </button>
        </div>
      </div>
    );
  };

  return (
    <RegistrationStepLayout
      icon={familyIcon}
      // title="Siblings"
      // subtitle="Sibling details help add more about your family background."

      title={title}
      subtitle={subtitle}
      stepText="Step 3 of 5"
      // onBack={onBack}
      onContinue={() => onNext(counts)}
      continueVariant="button"
    >
      <Counter
        label="Married Sisters"
        value={counts.marriedSisters}
        onChange={(v) => setCounts({ ...counts, marriedSisters: v })}
      />

      <Counter
        label="Unmarried Sisters"
        value={counts.unmarriedSisters}
        onChange={(v) => setCounts({ ...counts, unmarriedSisters: v })}
      />

      <Counter
        label="Married Brothers"
        value={counts.marriedBrothers}
        onChange={(v) => setCounts({ ...counts, marriedBrothers: v })}
      />

      <Counter
        label="Unmarried Brothers"
        value={counts.unmarriedBrothers}
        onChange={(v) => setCounts({ ...counts, unmarriedBrothers: v })}
      />

      {/* SKIP FOR NOW */}
      <div
        onClick={handleSkip}
        style={{
          marginTop: 10,
          width: "100%",
          height: 50,
          borderRadius: 50,
          border: "1px solid transparent",
          background:
            "linear-gradient(#fff, #fff) padding-box, linear-gradient(90deg, rgba(255,119,5,0.88), rgba(230,0,0,0.81)) border-box",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontWeight: 600,
          color: "rgba(230,0,0,0.81)",
          cursor: "pointer",
        }}
      >
        Skip for now
      </div>
    </RegistrationStepLayout>
  );
};

// =====================================================
// STEP 4 – ABOUT FAMILY
// =====================================================

const AboutFamilyStep = ({ onBack, onSubmit, showPopup, title, subtitle }) => {
  const [familyOrigin, setFamilyOrigin] = useState("");
  const [aboutFamily, setAboutFamily] = useState("");
  const [typing, setTyping] = useState(false);

  const [autoWriteUsed, setAutoWriteUsed] = useState(false);

  const [familyOriginError, setFamilyOriginError] = useState("");

  const FAMILY_ORIGIN_REGEX = /^[A-Za-z0-9 ]*$/;

  //   const typingIntervalRef = useRef(null);
  // const typingIndexRef = useRef(0);

  //   // 👇 typing animation
  // const typeText = (text) => {
  //   if (!text) return;

  //   // 🔒 Enforce 500 char limit
  //   const safeText = text.slice(0, 500);

  //   // 🧹 Clear any previous typing
  //   if (typingIntervalRef.current) {
  //     clearInterval(typingIntervalRef.current);
  //   }

  //   setTyping(true);
  //   setAboutFamily("");
  //   typingIndexRef.current = 0;

  //   typingIntervalRef.current = setInterval(() => {
  //     setAboutFamily((prev) => {
  //       if (typingIndexRef.current >= safeText.length) {
  //         clearInterval(typingIntervalRef.current);
  //         typingIntervalRef.current = null;
  //         setTyping(false);
  //         return prev;
  //       }

  //       const nextChar = safeText.charAt(typingIndexRef.current);
  //       typingIndexRef.current += 1;

  //       return prev + nextChar;
  //     });
  //   }, 25);
  // };

  // useEffect(() => {
  //   return () => {
  //     if (typingIntervalRef.current) {
  //       clearInterval(typingIntervalRef.current);
  //     }
  //   };
  // }, []);

  // 👇 auto write handler
  // const handleAutoWrite = async () => {
  //   try {
  //     const res = await getAutoWriteContent("about_family");
  //     if (res.data?.status) {
  //       typeText(res.data.value);
  //     }
  //   } catch (e) {
  //     console.error("Auto write failed", e);
  //   }
  // };

  const handleAutoWrite = async () => {
    if (autoWriteUsed) return;

    setTyping(true);
    setAutoWriteUsed(true); // 🔒 disable immediately after first click

    try {
      const res = await getAutoWriteContent("about_family");

      // ❌ API returned failure
      if (res.data?.status === false) {
        showPopup(res.data?.value || "Auto write failed.");
        return;
      }

      // ❌ status true BUT value missing
      if (!res.data?.value) {
        showPopup("Auto write content is currently unavailable.");
        return;
      }

      // ✅ SUCCESS
      setAboutFamily(res.data.value.slice(0, 500));
    } catch (e) {
      showPopup("Something went wrong while generating content.");
    } finally {
      setTyping(false);
    }
  };

  const handleContinue = async () => {
    if (familyOrigin && !FAMILY_ORIGIN_REGEX.test(familyOrigin)) {
      setFamilyOriginError("Only letters, numbers, and spaces are allowed.");
      return;
    }

    await submitFamilyOriginAbout({
      familyOrigin,
      // aboutFamily,
      aboutMe: aboutFamily,
    });

    onSubmit({ familyOrigin, aboutFamily });
  };

  const handleSkip = async () => {
    await submitFamilyOriginAbout({
      familyOrigin,
      // aboutFamily,
      aboutMe: aboutFamily,
    });

    onSubmit({ familyOrigin, aboutFamily });
  };

  return (
    <RegistrationStepLayout
      icon={familyIcon}
      // title="About Family"
      // subtitle="Share a few details about your family background."

      title={title}
      subtitle={subtitle}
      stepText="Step 4 of 5"
      // onBack={onBack}
      onContinue={handleContinue}
      continueVariant="button"
    >
      {/* FAMILY ORIGIN */}
      <div style={{ position: "relative", marginTop: 20, width: "100%" }}>
        <span style={floatingLabel}>Family Origin</span>
        <input
          value={familyOrigin}
          // onChange={(e) => setFamilyOrigin(e.target.value)}

          onChange={(e) => {
            const val = e.target.value;

            // 🔒 allow only alphanumeric + space
            if (!FAMILY_ORIGIN_REGEX.test(val)) {
              setFamilyOriginError(
                "Only letters, numbers, and spaces are allowed.",
              );
              return;
            }

            setFamilyOrigin(val);
            setFamilyOriginError("");
          }}
          placeholder="Enter your family origin"
          style={inputBox}
        />
      </div>

      {familyOriginError && (
        <div
          style={{
            color: "#ff3c3c",
            fontSize: 12,
            marginTop: 6,
            fontFamily: "Montserrat",
          }}
        >
          {familyOriginError}
        </div>
      )}

      {/* ABOUT FAMILY */}
      {/* ABOUT FAMILY HEADER + AUTO WRITE */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginTop: 18,
          width: "100%",
        }}
      >
        <span
          style={{
            fontSize: 15,
            fontWeight: 600,
            color: "#000",
          }}
        >
          About Family
        </span>

        <AITwinkleButton
          onClick={handleAutoWrite}
          isLoading={typing}
          //  isDisabled={typing}
          isDisabled={typing || autoWriteUsed}
        />
      </div>

      {/* ABOUT FAMILY TEXTAREA */}
      <textarea
        value={aboutFamily}
        onChange={(e) => setAboutFamily(e.target.value)}
        placeholder="Enter description about your family. "
        rows={4}
        style={textArea}
      />

      {/* SKIP FOR NOW */}
      <div
        onClick={handleSkip}
        style={{
          marginTop: 18,
          width: "100%",
          height: 50,
          borderRadius: 50,
          border: "1px solid transparent",
          background:
            "linear-gradient(#fff, #fff) padding-box, linear-gradient(90deg, rgba(255,119,5,0.88), rgba(230,0,0,0.81)) border-box",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontWeight: 600,
          color: "rgba(230,0,0,0.81)",
          cursor: "pointer",
        }}
      >
        Skip for now
      </div>
    </RegistrationStepLayout>
  );
};

// =====================================================
// STEP 5 – ABOUT me and my partner
// =====================================================

const AboutMePartnerStep = ({
  onBack,
  onCompleted,
  showPopup,
  title,
  subtitle,
}) => {
  const [aboutMe, setAboutMe] = useState("");
  const [aboutPartner, setAboutPartner] = useState("");
  const [typing, setTyping] = useState(false);

  const [loadingMe, setLoadingMe] = useState(false);
const [loadingPartner, setLoadingPartner] = useState(false);

  const profileCreatedById = Number(localStorage.getItem("profileCreatedById"));
  const gender = localStorage.getItem("selectedGender");

  const aboutMeText = resolveProfileUIText({
    profileCreatedById,
    gender,
    stepKey: "ABOUT_ME",
    stepConfig: PROFILE_UI_CONTENT.ABOUT_ME,
  });

  const aboutPartnerText = resolveProfileUIText({
    profileCreatedById,
    gender,
    stepKey: "ABOUT_PARTNER",
    stepConfig: PROFILE_UI_CONTENT.ABOUT_PARTNER,
  });

  const [autoWriteMeUsed, setAutoWriteMeUsed] = useState(false);
  const [autoWritePartnerUsed, setAutoWritePartnerUsed] = useState(false);

  const typeText = (setter, text) => {
    if (!text) return;

    setTyping(true);
    setter("");

    let index = 0;
    const interval = setInterval(() => {
      setter((prev) => {
        if (index >= text.length) {
          clearInterval(interval);
          setTyping(false);
          return prev;
        }
        return prev + text.charAt(index++);
      });
    }, 25);
  };

  // const handleAutoWrite = async (type) => {
  //   if (type === "about_me" && autoWriteMeUsed) return;
  //   if (type === "about_partner" && autoWritePartnerUsed) return;

  //   setTyping(true);

  //   if (type === "about_me") setAutoWriteMeUsed(true);
  //   if (type === "about_partner") setAutoWritePartnerUsed(true);

  //   try {
  //     const res = await getAutoWriteContent(type);

  //     if (res.data?.status === false) {
  //       showPopup(res.data?.value || "Auto write failed.");
  //       return;
  //     }

  //     if (!res.data?.value) {
  //       showPopup("Auto write content is currently unavailable.");
  //       return;
  //     }

  //     if (type === "about_me") {
  //       setAboutMe(res.data.value.slice(0, 500));
  //     }

  //     if (type === "about_partner") {
  //       setAboutPartner(res.data.value.slice(0, 500));
  //     }
  //   } catch (e) {
  //     showPopup("Something went wrong while generating content.");
  //   } finally {
  //     setTyping(false);
  //   }
  // };


  const handleAutoWrite = async (type) => {
  if (type === "about_me" && autoWriteMeUsed) return;
  if (type === "about_partner" && autoWritePartnerUsed) return;

  if (type === "about_me") {
    setLoadingMe(true);
    setAutoWriteMeUsed(true);
  }

  if (type === "about_partner") {
    setLoadingPartner(true);
    setAutoWritePartnerUsed(true);
  }

  try {
    const res = await getAutoWriteContent(type);

    if (res.data?.status === false) {
      showPopup(res.data?.value || "Auto write failed.");
      return;
    }

    if (!res.data?.value) {
      showPopup("Auto write content is currently unavailable.");
      return;
    }

    if (type === "about_me") {
      setAboutMe(res.data.value.slice(0, 500));
    }

    if (type === "about_partner") {
      setAboutPartner(res.data.value.slice(0, 500));
    }
  } catch (e) {
    showPopup("Something went wrong while generating content.");
  } finally {
    if (type === "about_me") setLoadingMe(false);
    if (type === "about_partner") setLoadingPartner(false);
  }
};


  const handleContinue = async () => {
    await submitAboutLifePartner({
      aboutMe,
      aboutLifePartner: aboutPartner,
    });

    onCompleted(); // ✅ EXIT FAMILY FLOW
  };

  const handleSkip = async () => {
    await submitAboutLifePartner({
      aboutMe,
      aboutLifePartner: aboutPartner,
    });

    onCompleted();
  };

  return (
    <RegistrationStepLayout
      icon={familyIcon}
      // title="About Me"
      // subtitle="Almost done! Add a detailed introduction."
      title={title}
      subtitle={subtitle}
      stepText="Step 5 of 5"
      // onBack={onBack}
      onContinue={handleContinue}
      continueVariant="button"
    >
      {/* ABOUT ME */}
      <SectionHeader
        // title="About me"
        //       title={aboutMeText.title}
        // subtitle={aboutMeText.subtitle}
        onAutoWrite={() => handleAutoWrite("about_me")}
        loading={loadingMe}
        disabled={autoWriteMeUsed}
      />
      <textarea
        value={aboutMe}
        onChange={(e) => setAboutMe(e.target.value)}
        placeholder="Write about family, values & lifestyle or use auto-write."
        style={textArea}
      />

      {/* ABOUT PARTNER */}
      <SectionHeader
        // title="About my partner"

        icon={aboutPartnerIcon}
        title={aboutPartnerText.title}
        subtitle={aboutPartnerText.subtitle}
        onAutoWrite={() => handleAutoWrite("about_partner")}
        loading={loadingPartner}
        disabled={autoWritePartnerUsed}
      />
      <textarea
        value={aboutPartner}
        onChange={(e) => setAboutPartner(e.target.value)}
        placeholder="Write about family, values & lifestyle or use auto-write."
        style={textArea}
      />

      {/* SKIP FOR NOW */}
      <div
        onClick={handleSkip}
        style={{
          marginTop: 18,
          width: "100%",
          height: 50,
          borderRadius: 50,
          border: "1px solid transparent",
          background:
            "linear-gradient(#fff, #fff) padding-box, linear-gradient(90deg, rgba(255,119,5,0.88), rgba(230,0,0,0.81)) border-box",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontWeight: 600,
          color: "rgba(230,0,0,0.81)",
          cursor: "pointer",
        }}
      >
        Skip for now
      </div>
    </RegistrationStepLayout>
  );
};

//
// =====================================================
// UI ATOMS (same look & feel)
// =====================================================
//
const SectionHeader = ({
  icon,
  title,
  subtitle,
  onAutoWrite,
  loading,
  disabled,
}) => (
  <div style={{ width: "100%", marginTop: 4 }}>
    <div
      style={{
        display: "flex",
        width: "100%",
        justifyContent: "space-between",
        alignItems: "center",
        marginTop: 8,
      }}
    >
      {/* LEFT: ICON + TITLE */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
        }}
      >
        {icon && (
          <img
            src={icon}
            alt="section icon"
            style={{
              width: 26,
              height: 26,
              objectFit: "contain",
            }}
          />
        )}

        <span style={{ fontSize: 19, fontWeight: 600 }}>{title}</span>
      </div>

      <AITwinkleButton
        onClick={onAutoWrite}
        isLoading={loading}
        isDisabled={disabled}
      />
    </div>
    {subtitle && (
      <div style={{ fontSize: 13, color: "#777", marginTop: 4 }}>
        {subtitle}
      </div>
    )}
  </div>
);

const Input = ({ label, value, onChange, placeholder }) => (
  <div style={{ marginTop: 25 }}>
    <div style={{ fontSize: 13, color: "#888", marginBottom: 6 }}>{label}</div>
    <input
      value={value}
      placeholder={placeholder}
      onChange={(e) => onChange(e.target.value)}
      style={inputStyle}
    />
  </div>
);

const TextArea = ({ value, onChange, placeholder }) => (
  <textarea
    value={value}
    placeholder={placeholder}
    onChange={(e) => onChange(e.target.value)}
    style={{
      ...inputStyle,
      height: 90,
      resize: "none",
      marginTop: 25,
    }}
  />
);

const OptionButton = ({ label, active, onClick }) => (
  <div
    onClick={onClick}
    style={{
      height: 54,
      borderRadius: 50,
      marginBottom: 14,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      cursor: "pointer",
      fontWeight: 600,
      border: active ? "1px solid transparent" : "1px solid #e8e6ea",
      background: active
        ? "linear-gradient(180deg, rgba(255,119,5,0.88), rgba(230,0,0,0.81))"
        : "#fff",
      color: active ? "#fff" : "#555",
    }}
  >
    {label}
  </div>
);

const SkipButton = ({ onClick }) => (
  <div
    onClick={onClick}
    style={{
      marginTop: 25,
      height: 50,
      borderRadius: 50,
      border: "1px solid rgba(230,0,0,0.81)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      color: "rgba(230,0,0,0.81)",
      fontWeight: 600,
      cursor: "pointer",
    }}
  >
    Skip for now
  </div>
);

const InfoBox = ({ text }) => (
  <div
    style={{
      marginTop: 15,
      padding: 14,
      border: "1px dashed rgba(230,0,0,0.5)",
      borderRadius: 12,
      fontSize: 12,
      color: "#555",
      textAlign: "center",
    }}
  >
    {text}
  </div>
);

const inputStyle = {
  width: "100%",
  height: 50,
  borderRadius: 12,
  border: "1px solid #e8e6ea",
  padding: "0 14px",
  fontSize: 14,
};

const floatingLabel = {
  position: "absolute",
  top: -9,
  left: 18,
  background: "#fff",
  padding: "0 8px",
  fontSize: 14,
  fontWeight: 600,
  zIndex: 1,
};

const inputBox = {
  width: "100%",
  height: 58,
  borderRadius: 18,
  border: "1.5px solid #E6E6EA",
  padding: "0 18px",
  fontSize: 15,
  outline: "none",
};

const textArea = {
  marginTop: 10,
  width: "100%",
  minHeight: 120,
  borderRadius: 22,
  border: "1.5px solid #E6E6EA",
  padding: "18px",
  fontSize: 15,
  resize: "none",
};

const autoWriteBtn = {
  position: "absolute",
  right: 14,
  bottom: 14,
  borderRadius: 20,
  padding: "6px 14px",
  fontSize: 12,
  border: "none",
  background:
    "linear-gradient(180deg, rgba(255,119,5,0.88), rgba(230,0,0,0.81))",
  color: "#fff",
  cursor: "pointer",
};

const AITwinkleButton = ({ onClick, isLoading, isDisabled }) => {
  return (
    <button
      onClick={onClick}
      // disabled={isLoading}
      disabled={isLoading || isDisabled}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 6,
        padding: "6px 14px",
        borderRadius: 20,
        border: "2px solid rgba(255,255,255,0.9)",
        // background: isLoading
        //   ? "#ccc"
        //   : "linear-gradient(180deg, rgba(255,119,5,0.88), rgba(230,0,0,0.81))",
        color: "#fff",
        fontSize: 11,
        fontWeight: 800,
        cursor: isLoading ? "not-allowed" : "pointer",
        background:
          isLoading || isDisabled
            ? "#d3d3d3"
            : "linear-gradient(180deg, rgba(255,119,5,0.88), rgba(230,0,0,0.81))",

        cursor: isLoading || isDisabled ? "not-allowed" : "pointer",

        boxShadow:
          isLoading || isDisabled ? "none" : "0 0 18px rgba(255,119,5,0.6)",
      }}
    >
      {/* {isLoading ? "Writing" : "Auto write"} */}

      {isLoading ? (
  <span className="wave-text">
    {"Writing...".split("").map((char, i) => (
      <span key={i} style={{ animationDelay: `${i * 0.08}s` }}>
        {char}
      </span>
    ))}
  </span>
) : (
  "Auto write"
)}

      {/* ⭐ Twinkle Star */}
      <img
        src={threestar}
        alt="star"
        style={{
          width: 14,
          height: 14,
          animation: "twinkle 1s infinite alternate",
        }}
      />
    </button>
  );
};


<style>
{`
.wave-text span {
  display: inline-block;
  animation: wave 1s ease-in-out infinite;
}

@keyframes wave {
  0% { transform: translateY(0px); }
  50% { transform: translateY(-3px); }
  100% { transform: translateY(0px); }
}
`}
</style>